/**
 * Route manifest for the CORE rent pages (S2 port).
 *
 * `routes.tsx` is generated from `PAGE_REGISTRY` and is not edited by feature
 * work; this module is what the orchestrator drops in for the registry's
 * `rent` entry. It exports the eight core rent routes and nothing else — the
 * remaining rent sub-pages (expenses, brokers, tds, reports, statements,
 * maintenance, vacancy, landlord, settings) ship their own manifest and are
 * composed alongside these under the same layout.
 *
 * Paths are RELATIVE to `/rent`, matching how `PAGE_REGISTRY` maps
 * `matchPrefix: "/rent"` to a single routed subtree. Adding a route here does
 * NOT add a nav entry: the navbar is the fixed 7-entry `NAV_PAGES` set and
 * rent sub-pages are tabs/sub-routes inside the one `Rent` entry.
 *
 * SAFE-04: every route below sits inside the registry's `/rent` prefix, so it
 * is gated by exactly the check `middleware.ts` applies today — `RequireAuth`
 * -> `guardRoute` -> `resolvePermission("rent", "view")` — and the FastAPI
 * side additionally enforces `rentModuleAccess` on every call these pages
 * make. No route here is reachable by anyone who cannot reach `/rent` now.
 *
 * ── Wiring ────────────────────────────────────────────────────────────────
 * Two shapes are exported so either style of wiring works:
 *
 *   // (a) registry route is `{ path: "rent/*", element: … }` (today's shape)
 *   IMPLEMENTED.rent = <RentCoreRoutes />
 *
 *   // (b) registry route becomes `{ path: "rent", children: […] }`
 *   children: rentCoreRoutesWithLayout
 *
 * Both mount `RentLayout` as a pathless parent so the rent header, sub-nav and
 * payment FAB persist across navigation inside the module.
 */
import { Suspense, type ReactNode } from "react";
import { useRoutes, type RouteObject } from "react-router-dom";
import { RentLayout } from "@/components/rent/core/RentLayout";
import { lazyNamed, RouteSkeleton } from "@/lib/prefetch";

// S3: lazy per leaf page (not just per `/rent` subtree) — a USER with rent
// access but no admin grant still only ever taps a handful of these tabs, and
// the eight leaves here plus the thirteen in `rent-extras.routes.tsx` would
// otherwise be one ~300-500KB slab downloaded on the FIRST rent tab visited.
const RentDashboardPage = lazyNamed(
  () => import("@/pages/rent/index/RentDashboardPage"),
  "RentDashboardPage",
);
const PropertiesPage = lazyNamed(
  () => import("@/pages/rent/properties/PropertiesPage"),
  "PropertiesPage",
);
const PropertyDetailPage = lazyNamed(
  () => import("@/pages/rent/properties/PropertyDetailPage"),
  "PropertyDetailPage",
);
const TenantsPage = lazyNamed(() => import("@/pages/rent/tenants/TenantsPage"), "TenantsPage");
const TenantDetailPage = lazyNamed(
  () => import("@/pages/rent/tenants/TenantDetailPage"),
  "TenantDetailPage",
);
const AgreementsPage = lazyNamed(
  () => import("@/pages/rent/agreements/AgreementsPage"),
  "AgreementsPage",
);
const AgreementDetailPage = lazyNamed(
  () => import("@/pages/rent/agreements/AgreementDetailPage"),
  "AgreementDetailPage",
);
const PaymentsPage = lazyNamed(() => import("@/pages/rent/payments/PaymentsPage"), "PaymentsPage");

function s(element: ReactNode): ReactNode {
  return <Suspense fallback={<RouteSkeleton />}>{element}</Suspense>;
}

/**
 * The eight leaf routes, WITHOUT the layout — so the other rent manifest can
 * be concatenated with these under one shared `RentLayout` parent rather than
 * each module mounting its own header.
 */
export const rentCoreRoutes: RouteObject[] = [
  { index: true, element: s(<RentDashboardPage />) },
  { path: "properties", element: s(<PropertiesPage />) },
  { path: "properties/:id", element: s(<PropertyDetailPage />) },
  { path: "tenants", element: s(<TenantsPage />) },
  { path: "tenants/:id", element: s(<TenantDetailPage />) },
  { path: "agreements", element: s(<AgreementsPage />) },
  { path: "agreements/:id", element: s(<AgreementDetailPage />) },
  { path: "payments", element: s(<PaymentsPage />) },
];

/** The same routes wrapped in the rent chrome — drop-in for a `children` slot. */
export const rentCoreRoutesWithLayout: RouteObject[] = [
  { element: <RentLayout />, children: rentCoreRoutes },
];

/**
 * Element form, for the current `rent/*` splat route in `routes.tsx`.
 * `useRoutes` resolves the manifest relative to the splat's matched prefix.
 */
export function RentCoreRoutes() {
  return useRoutes(rentCoreRoutesWithLayout);
}

export { RentLayout };
export default rentCoreRoutes;
